import React, { Component } from 'react';

import PropTypes from 'prop-types';
import { hot } from 'react-hot-loader/root';
import { compose } from 'redux';

import { DevelopersCompanyContext } from 'common/containers/DevelopersCompanyContainer';
import publicConfig from 'common/core/publicConfig';
import MultiLanguageCodeBlock from 'common/developers/docs/widget/MultiLanguageCodeBlock';
import Button from 'common/inputs/Button';
import Link from 'common/Link';
import withContexts from 'common/util/withContexts';

import 'css/components/developers/docs/changelog/_ChangelogDocs.scss';

class ChangelogDocs extends Component {
  static propTypes = {
    company: PropTypes.object,
  };

  componentDidMount() {
    if (__SSR_TEST_RUNNER__) {
      return;
    }

    Canny('initChangelog', {
      appID: publicConfig('cannyCompanyID'),
      align: 'top',
      position: 'right',
      theme: 'auto',
    });
  }

  componentWillUnmount() {
    if (__SSR_TEST_RUNNER__) {
      return;
    }

    Canny('closeChangelog');
  }

  render() {
    const { company } = this.props;
    const companyID = company?._id ? company._id : 'YOUR_APP_ID';
    return (
      <div className="changelogDocs">
        <div className="title">Changelog&nbsp;Widget</div>
        <div className="text">
          The changelog widget lets you embed your changelog into your&nbsp;website.
        </div>
        <div className="text">
          It works by attaching itself to a button. Whenever the button is clicked, the changelog
          will open.
        </div>
        <div className="text">Here's a live example that loads Canny's changelog widget:</div>
        <div className="buttonContainer" data-canny-changelog>
          <Button value="Try Me!" />
        </div>
        <div className="step">
          <div className="subheading">1. Install our client-side SDK</div>
          <div className="text">
            Our client-side SDK lets you perform Canny-related actions on your website. This
            includes <Link to="/install">identifying your users</Link> and adding widgets.
          </div>
          <div className="text">
            Add the following code anywhere inside the <span className="code">&lt;head&gt;</span> or{' '}
            <span className="code">&lt;body&gt;</span> tags.
          </div>
          <MultiLanguageCodeBlock
            blocks={[
              {
                code: InstallSDKSnippet,
                language: 'html',
              },
            ]}
            showSelector={false}
          />
        </div>
        <div className="step">
          <div className="subheading">
            2. Add <span className="code">data-canny-changelog</span> to your button
          </div>
          <div className="text">
            The <span className="code">data-canny-changelog</span> attribute tells us which element
            is your button.
          </div>
          <div className="text">
            Once the changelog is initialized, we will listen for clicks on elements with this
            attribute, to trigger the changelog to open.
          </div>
          <MultiLanguageCodeBlock
            blocks={[
              {
                code: ButtonSnippet,
                language: 'html',
              },
            ]}
            showSelector={false}
          />
        </div>
        <div className="step">
          <div className="subheading">3. Initialize the changelog</div>
          <div className="text">
            The final step is to tell our SDK to initialize the widget. You can only initialize the
            widget once, re-initializations may result in errors or unpredictable behaviour.
          </div>
          <div className="text">
            If your button element is created at page load, you can simply add the HTML snippet
            anywhere after the code from step #1.
          </div>
          <div className="text">
            If your button element might be rendered after page load, then you'll want to call our
            SDK after the button is created.
          </div>
          <MultiLanguageCodeBlock
            blocks={[
              {
                code: InitializeHTMLSnippet.replace('YOUR_APP_ID', companyID),
                name: 'HTML',
                language: 'html',
              },
              {
                code: InitializeReactSnippet.replace('YOUR_APP_ID', companyID),
                name: 'React',
                language: 'js',
              },
              {
                code: InitializeAngularSnippet.replace('YOUR_APP_ID', companyID),
                name: 'Angular',
                language: 'js',
              },
              {
                code: InitializeVueSnippet.replace('YOUR_APP_ID', companyID),
                name: 'Vue.js',
                language: 'js',
              },
            ]}
          />
        </div>
      </div>
    );
  }
}

const ButtonSnippet = `<button data-canny-changelog>\n  View Changelog\n</button>`;
const InstallSDKSnippet = `<script>!function(w,d,i,s){function l(){if(!d.getElementById(i)){var f=d.getElementsByTagName(s)[0],e=d.createElement(s);e.type="text/javascript",e.async=!0,e.src="https://canny.io/sdk.js",f.parentNode.insertBefore(e,f)}}if("function"!=typeof w.Canny){var c=function(){c.q.push(arguments)};c.q=[],w.Canny=c,"complete"===d.readyState?l():w.attachEvent?w.attachEvent("onload",l):w.addEventListener("load",l,!1)}}(window,document,"canny-jssdk","script");</script>`;
const InitializeAngularSnippet = `ngAfterViewInit() {\n  Canny('initChangelog', {\n    appID: 'YOUR_APP_ID',\n    position: 'bottom',\n    align: 'left',\n    theme: 'light', // options: light [default], dark, auto\n });\n}`;
const InitializeHTMLSnippet = `<script>\n  Canny('initChangelog', {\n    appID: 'YOUR_APP_ID',\n    position: 'bottom',\n    align: 'left',\n    theme: 'light', // options: light [default], dark, auto\n});\n</script>`;
const InitializeReactSnippet = `componentDidMount() {\n  Canny('initChangelog', {\n    appID: 'YOUR_APP_ID',\n    position: 'bottom',\n    align: 'left',\n    theme: 'light', // options: light [default], dark, auto\n });\n}`;
const InitializeVueSnippet = `mounted() {\n  Canny('initChangelog', {\n    appID: 'YOUR_APP_ID',\n    position: 'bottom',\n    align: 'left',\n    theme: 'light', // options: light [default], dark, auto\n });\n}`;

export default compose(withContexts({ company: DevelopersCompanyContext }), hot)(ChangelogDocs);
